import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiTrendingDown, FiCalendar, FiDollarSign, FiPackage } from 'react-icons/fi';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getSalesTrends, getPerformanceData } from '../services/analytics';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import toast from 'react-hot-toast';

export default function Analytics() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');
  const [salesTrends, setSalesTrends] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [driverPerformance, setDriverPerformance] = useState([]);
  const [guidePerformance, setGuidePerformance] = useState([]);
  const [companyPerformance, setCompanyPerformance] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [trendsData, driversData, guidesData, companiesData] = await Promise.all([
        getSalesTrends(period),
        getPerformanceData('drivers'),
        getPerformanceData('tour_guides'),
        getPerformanceData('tour_companies')
      ]);
      setSalesTrends(trendsData.data?.salesTrends || []);
      setForecast(trendsData.data?.forecast || []);
      setDriverPerformance(driversData.data || []);
      setGuidePerformance(guidesData.data || []);
      setCompanyPerformance(companiesData.data || []);
    } catch (error) {
      toast.error('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => `$${value.toLocaleString()}`;

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Analytics Dashboard</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setPeriod('daily')}
            className={`px-3 py-2 rounded-lg ${period === 'daily' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            Daily
          </button>
          <button
            onClick={() => setPeriod('weekly')}
            className={`px-3 py-2 rounded-lg ${period === 'weekly' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            Weekly
          </button>
          <button
            onClick={() => setPeriod('monthly')}
            className={`px-3 py-2 rounded-lg ${period === 'monthly' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            Monthly
          </button>
        </div>
      </div>

      {/* Sales Trends Chart */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Sales Trends</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={salesTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Line type="monotone" dataKey="total" stroke="#006994" strokeWidth={2} name="Revenue" />
              <Line type="monotone" dataKey="count" stroke="#20B2AA" strokeWidth={2} name="Orders" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Forecast */}
      {forecast.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Revenue Forecast</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {forecast.map((item, idx) => (
              <div key={idx} className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg p-4 text-white">
                <p className="text-sm opacity-80">{item.period}</p>
                <p className="text-2xl font-bold">{formatCurrency(item.predicted)}</p>
                <p className="text-xs opacity-70 mt-2">Predicted revenue</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Driver Performance */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <FiTrendingUp className="mr-2 text-blue-500" /> Top Drivers
          </h2>
          {driverPerformance.length > 0 ? (
            <div className="space-y-3">
              {driverPerformance.slice(0, 5).map((driver, idx) => (
                <div key={driver.id} className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-800">{driver.name}</p>
                    <p className="text-xs text-gray-500">{driver.completed_orders} orders</p>
                  </div>
                  <p className="font-bold text-primary-600">${driver.total_earnings}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No data available</p>
          )}
        </div>

        {/* Tour Guide Performance */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <FiTrendingUp className="mr-2 text-purple-500" /> Top Tour Guides
          </h2>
          {guidePerformance.length > 0 ? (
            <div className="space-y-3">
              {guidePerformance.slice(0, 5).map((guide, idx) => (
                <div key={guide.id} className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-800">{guide.name}</p>
                    <p className="text-xs text-gray-500">{guide.total_orders} tours</p>
                  </div>
                  <p className="font-bold text-primary-600">${guide.total_earnings}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No data available</p>
          )}
        </div>

        {/* Company Performance */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <FiTrendingUp className="mr-2 text-green-500" /> Top Companies
          </h2>
          {companyPerformance.length > 0 ? (
            <div className="space-y-3">
              {companyPerformance.slice(0, 5).map((company, idx) => (
                <div key={company.id} className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-800">{company.name}</p>
                    <p className="text-xs text-gray-500">{company.total_orders} bookings</p>
                  </div>
                  <p className="font-bold text-primary-600">${company.total_earnings}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No data available</p>
          )}
        </div>
      </div>
    </div>
  );
}
